import { Injectable } from '@angular/core';
import { DataManipulationConfig, OptionState, OptionStateWithCount, SortDirectionType } from '../../models';
import { FilterPanelFacadeService } from '../filter-panel-facade/filter-panel-facade.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterPanelPartition } from '../../constants/filter-panel.constant';
import { DataFilterManagerService } from '../data-filter-manager/data-filter-manager.service';
import { v4 as uuidv4 } from 'uuid';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable()
export class FilterPanelService {
  private readonly filterInstanceId$ = new BehaviorSubject<string>(null);

  constructor(
    private filterPanelFacadeService: FilterPanelFacadeService,
    private dataFilterService: DataFilterManagerService
  ) {}

  init<TData>(filterDefinition: DataManipulationConfig<TData>, shouldOpen?: boolean): void {
    let filterInstanceId = filterDefinition.filterInstanceId;

    if (!filterInstanceId) {
      filterInstanceId = this.generateInstanceId();
    }

    this.filterInstanceId$.next(filterInstanceId);

    this.dataFilterService.init({ ...filterDefinition, filterInstanceId: filterInstanceId });
    this.filterPanelFacadeService.init(filterInstanceId);

    if (shouldOpen) {
      this.open();
    }
  }

  isOpen(): Observable<boolean> {
    return this.filterPanelFacadeService.isOpen();
  }

  setData<TData>(data: TData[]): void {
    this.dataFilterService.setData(this.filterInstanceId$.getValue(), data);
  }

  toggleOptions(options: { [fieldId: string]: string[] }): void {
    this.dataFilterService.toggleOptions(this.filterInstanceId$.getValue(), options);
  }

  selectOptions(optionsData: { [fieldId: string]: string[] }): void {
    this.dataFilterService.selectOptions(this.filterInstanceId$.getValue(), optionsData);
  }

  unSelectOptions(optionsData: { [fieldId: string]: string[] }): void {
    this.dataFilterService.unSelectOptions(this.filterInstanceId$.getValue(), optionsData);
  }

  setOptions(optionsdata: { [fieldId: string]: string[] }): void {
    this.dataFilterService.setOptions(this.filterInstanceId$.getValue(), optionsdata);
  }

  getFilteringOptions(): Observable<{
    [key: string]: { [key: string]: OptionState };
  }> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.getFilteringOptions(filterInstanceId));
  }

  getFieldRelatedActiveOptions(fieldId: string): Observable<OptionState[]> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getFieldRelatedActiveOptions(filterInstanceId, fieldId)
    );
  }

  getFieldRelatedNestedDataIds(fieldId: string): Observable<Set<string>> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getFieldRelatedNestedDataIds(filterInstanceId, fieldId)
    );
  }

  getGroupRelatedNestedDataIds(nestedGroupId: string): Observable<Set<string>> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getGroupRelatedNestedDataIds(filterInstanceId, nestedGroupId)
    );
  }

  getOptionState(fieldId: string, optionId: string): Observable<OptionState> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getOptionState(filterInstanceId, fieldId, optionId)
    );
  }

  getOptionCount(fieldId: string, optionId: string): Observable<number> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getOptionCount(filterInstanceId, fieldId, optionId)
    );
  }

  getOptionsWithCountForField(fieldId: string): Observable<OptionStateWithCount[]> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getOptionsWithCountForFieldSelector(filterInstanceId, fieldId),
    );
  }

  getSortOptionId(): Observable<string> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getSortOptionId(filterInstanceId)
    );
  }

  getSortDirection(): Observable<SortDirectionType> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getSortDirection(filterInstanceId)
    );
  }

  changeSortOption(sortOptionId: string, sortDirection?: SortDirectionType): void {
    this.dataFilterService.changeSortOption(this.filterInstanceId$.getValue(), sortOptionId, sortDirection);
  }

  toggleSortDirection(): void {
    this.dataFilterService.toggleSortDirection(this.filterInstanceId$.getValue());
  }

  getSearchText(): Observable<string> {
    return this.assignInstanceId((filterInstanceId) =>
      this.dataFilterService.getSearchText(filterInstanceId)
    );
  }

  isSearchActive(): Observable<boolean> {
    return this.getSearchText().pipe(map((searchText) => !!searchText));
  }

  isEmptyBySearch(): Observable<boolean> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.isEmptyBySearch(filterInstanceId));
  }

  isEmptyByFilter(): Observable<boolean> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.isEmptyByFilter(filterInstanceId));
  }

  open(): void {
    this.filterPanelFacadeService.open();
  }

  getAllData<TData>(): Observable<TData[]> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.getAllData(filterInstanceId));
  }

  getFilteredData<TData>(): Observable<TData[]> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.getDataFilterEvent(filterInstanceId));
  }

  getSortedData<TData>(): Observable<TData[]> {
    return this.assignInstanceId((filterInstanceId) => this.dataFilterService.getSortedData(filterInstanceId));
  }

  reset(): void {
    this.dataFilterService.reset(this.filterInstanceId$.getValue());
  }

  resetField(fieldId: string): void {
    this.dataFilterService.resetField(this.filterInstanceId$.getValue(), fieldId);
  }

  resetSearch(): void {
    this.dataFilterService.resetSearch(this.filterInstanceId$.getValue());
  }

  close(destroyComponent?: boolean): void {
    if (destroyComponent) {
      this.dataFilterService.destroy(this.filterInstanceId$.getValue());
    }

    this.filterPanelFacadeService.close(destroyComponent as true);
  }

  private assignInstanceId<T>(callback: (filterInstanceId: string) => Observable<T>): Observable<T> {
    return this.filterInstanceId$.pipe(filter((filterInstanceId) => !!filterInstanceId), switchMap((filterInstanceId) => callback(filterInstanceId)));
  }

  private generateInstanceId(): string {
    return `${FilterPanelPartition}-${uuidv4()}`;
  }
}
